import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import empresaService from '../../services/empresaService';
import { invalidarCatalogo } from '../../services/catalogoCache';
import { PrimaryButton, BaseButton } from '../../components/common/Buttons';
import { useAuth } from '../../hooks/useAuth';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';

const PageContainer = styled.div`
  width: 100%;
  height: calc((100vh / var(--app-zoom, 1)) - 120px);
  padding: 1.25rem;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  overflow: hidden;
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Header = styled.div`
  margin-bottom: 0.2rem;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.info};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.txtSecundario};
`;

const Form = styled.form`
  background: ${({ theme }) => theme.colors.bgSecundario};
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  box-sizing: border-box;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding-right: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Col = styled.div`
  flex: 1;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

/* Textarea styled component removed (unused) */

const LogoPreview = styled.img`
  max-width: 160px;
  max-height: 100px;
  display: block;
  margin-top: 0.5rem;
  object-fit: contain;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.8rem;
  padding-top: 0.8rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.transparent};
  flex-shrink: 0;
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => `${theme.colors.danger}22`};
  color: ${({ theme }) => theme.colors.danger};
  padding: .75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.info};
  margin: 0.5rem 0 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const ToggleInput = styled.input`
  margin-top: 0.2rem;
  width: 1.1rem;
  height: 1.1rem;
  accent-color: ${({ theme }) => theme.colors.info};
  cursor: inherit;
`;

const ToggleTexts = styled.div`
  flex: 1;
`;

const ToggleLabel = styled.div`
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-weight: 600;
`;

const ToggleHelp = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 0.85rem;
  margin-top: 0.2rem;

  > div {
    margin: 0;
  }
`;

const ConfiguracionEmpresa = () => {
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const puedeEditarStock = hasPermission('empresa', 'change');

  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [error, setError] = useState('');

  // form fields
  const [nombre, setNombre] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [cuit, setCuit] = useState('');
  const [email, setEmail] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoOscuroFile, setLogoOscuroFile] = useState(null);
  const [logoOscuroUrl, setLogoOscuroUrl] = useState('');
  const [gestionaStockAlmacen, setGestionaStockAlmacen] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await empresaService.obtener();
        // empresaService puede devolver array o object con results
        const list = data.results || data || [];
        const first = Array.isArray(list) ? list[0] : list;
        if (first) {
          setEmpresa(first);
          setNombre(first.nombre || '');
          setRazonSocial(first.razon_social || '');
          setTelefono(first.telefono || '');
          setDireccion(first.direccion || '');
          setCuit(first.cuit || '');
          setEmail(first.email || '');
          setLogoUrl(first.logo || '');
          setLogoOscuroUrl(first.logo_oscuro || '');
          setGestionaStockAlmacen(first.gestiona_stock_almacen !== false);
        }
      } catch (err) {
        console.error(err);
        setError('Error al cargar los datos de la organización');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleLogoChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setLogoFile(f || null);
    if (f) setLogoUrl(URL.createObjectURL(f));
  };

  const handleLogoOscuroChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setLogoOscuroFile(f || null);
    if (f) setLogoOscuroUrl(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('nombre', nombre);
      payload.append('razon_social', razonSocial);
      payload.append('telefono', telefono);
      payload.append('direccion', direccion);
      payload.append('cuit', cuit);
      payload.append('email', email);
      if (logoFile) payload.append('logo', logoFile);
      if (logoOscuroFile) payload.append('logo_oscuro', logoOscuroFile);
      if (puedeEditarStock) payload.append('gestiona_stock_almacen', gestionaStockAlmacen);

      if (empresa && empresa.id) {
        const updated = await empresaService.actualizar(empresa.id, payload);
        setEmpresa(updated);
        setLogoUrl(updated.logo || '');
        setLogoOscuroUrl(updated.logo_oscuro || '');
      } else {
        const created = await empresaService.crear(payload);
        setEmpresa(created);
        setLogoUrl(created.logo || '');
        setLogoOscuroUrl(created.logo_oscuro || '');
      }
      // 'empresa' y 'empresa-config' quedan cacheados 5 min (ver catalogoCache);
      // sin invalidar, un cambio de logo o de gestiona_stock_almacen tardaría
      // hasta 5 min en verse en las pantallas que ya los pidieron (p.ej. OTC).
      invalidarCatalogo('empresa');
    } catch (err) {
      console.error(err);
      setError(err.response?.data || 'Error al guardar la organización');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Content>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title><i className="fas fa-sitemap"></i> Organización</Title>
            <HelpToggleBtn
              type="button"
              $active={helpOpen}
              onClick={() => setHelpOpen(v => !v)}
            >
              <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
              Ayuda
            </HelpToggleBtn>
          </div>
          <Description>Datos de la organización principal: nombre, razón social, CUIT, contacto y logo</Description>
        </Header>

        {helpOpen && (
          <div style={{ marginBottom: '1rem' }}>
            <HelpPanel
              tips={[
                'Estos datos identifican a tu organización y se usan en los documentos que emite el sistema.',
                'El logo aparece en las órdenes de trabajo y planillas impresas: cargalo con buena resolución.',
                'Podés cargar una versión del logo para tema claro y otra para tema oscuro.',
                'No confundir con Empresas: acá va la organización principal, allá las razones sociales de los equipos.',
              ]}
              nextSteps={[
                'Revisar cómo se ve el logo imprimiendo una orden de trabajo en blanco.',
              ]}
              inlineOpen
            />
          </div>
        )}

        {error && <ErrorMessage>{typeof error === 'string' ? error : JSON.stringify(error)}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <ScrollArea>
          <Row>
            <Col>
              <Label>Nombre</Label>
              <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </Col>
            <Col>
              <Label>Razón social</Label>
              <Input value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} />
            </Col>
          </Row>

          <Row>
            <Col>
              <Label>CUIT</Label>
              <Input value={cuit} onChange={(e) => setCuit(e.target.value)} />
            </Col>
            <Col>
              <Label>Teléfono</Label>
              <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} />
            </Col>
          </Row>

          <Row>
            <Col>
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </Col>
            <Col>
              <Label>Dirección</Label>
              <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            </Col>
          </Row>

          <Row>
            <Col>
              <Label>Logo (tema claro / impresión)</Label>
              <Input type="file" accept="image/*" onChange={handleLogoChange} />
              {logoUrl && <LogoPreview src={logoUrl} alt="Logo tema claro" />}
            </Col>
            <Col>
              <Label>Logo (tema oscuro)</Label>
              <Input type="file" accept="image/*" onChange={handleLogoOscuroChange} />
              {logoOscuroUrl
                ? <LogoPreview src={logoOscuroUrl} alt="Logo tema oscuro" style={{ background: theme.colors.previewDarkBg }} />
                : <div style={{ marginTop: '0.5rem', fontSize: '11px', color: theme.colors.txtSecondary }}>No configurado — se usará el logo claro</div>
              }
            </Col>
          </Row>

          <SectionTitle>Gestión de repuestos</SectionTitle>
          <ToggleRow $disabled={!puedeEditarStock}>
            <ToggleInput
              type="checkbox"
              checked={gestionaStockAlmacen}
              disabled={!puedeEditarStock}
              onChange={(e) => setGestionaStockAlmacen(e.target.checked)}
            />
            <ToggleTexts>
              <ToggleLabel>La organización gestiona la entrega de repuestos desde almacén</ToggleLabel>
              <ToggleHelp>
                <div>Activado: los repuestos deben entregarse desde almacén antes de marcarse como usados en las órdenes, y no se puede cerrar ni eliminar dejando entregas sin devolver.</div>
                <div>Desactivado: el administrativo gestiona los repuestos de las OTC/OTP libremente, sin depender del módulo de almacén. PUeden completarse las órdenes sin necesidad de devolución de repuestos, o sin que los repuestos tengan que ser entregados con anterioridad.</div>
                {!puedeEditarStock && <div> Solo un administrador puede cambiar esta opción.</div>}
              </ToggleHelp>
            </ToggleTexts>
          </ToggleRow>

          </ScrollArea>
          <ButtonGroup>
            <BaseButton type="button" onClick={() => window.location.href = '/configuracion'}>Volver</BaseButton>
            <PrimaryButton type="submit" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</PrimaryButton>
          </ButtonGroup>
        </Form>
      </Content>
    </PageContainer>
  );
};

export default ConfiguracionEmpresa;
